import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import { isEmpty } from 'ramda'
import * as yup from 'yup'
import { useInterval } from 'react-use'
import { useDispatch, useSelector } from 'react-redux'

import { apiStateSelectors } from '../../../../api/apiState'
import { walletApiEvents, walletApiSelectors } from '../../../../api/wallet'
import { FormLoader, TextField, TextInput } from '../../../../../views/components'
import { walletTransferEvents } from '../../state'
import { $TSFixMe } from 'types/ts-migrate'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'

const validationSchema = yup.object().shape({
  from: yup.string().required('Required.'),
  to: yup.string().required('Required.'),
})

const TransferReferenceLookup = ({ onSuccess }: any) => {
  const dispatch = useDispatch()
  const [wallets, setWallets] = useState({ from: null, to: null })
  const [lookupStarted, setLookupStarted] = useState(false)
  const isLookingUpReferences = useSelector(state =>
    apiStateSelectors.isLoading(state)([walletTransferEvents.walletLookup_request]),
  )
  const getReferenceLookupByReference = useSelector(walletApiSelectors.getReferenceLookupByReference)
  const getGlobalBalanceAsset = useSelector(walletApiSelectors.getGlobalBalanceAsset)
  const fromWallet: $TSFixMe = getReferenceLookupByReference(wallets.from)
  const toWallet: $TSFixMe = getReferenceLookupByReference(wallets.to)
  // An owner with a consolidated wallet keeps their funds there, not in the NAL the lookup
  // returns; the balances (and the transfer's fromAccount) follow the same ref the epic fetched.
  const fromRef = walletApiSelectors.spendableWalletRef(fromWallet)
  const toRef = walletApiSelectors.spendableWalletRef(toWallet)
  const fromBalance = getGlobalBalanceAsset(fromRef) || { balance: 0 }
  const toBalance = getGlobalBalanceAsset(toRef) || { balance: 0 }
  const toWalletExists = useSelector(state => walletApiSelectors.walletExists(state)(toRef))
  const lookupFailed = lookupStarted && !isLookingUpReferences && !(fromWallet && toWallet)

  useEffect(() => {
    if (wallets.to && wallets.from) {
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
      dispatch(walletTransferEvents.walletLookup_request(wallets))
    }
  }, [dispatch, wallets])

  useEffect(() => {
    if (isLookingUpReferences) setLookupStarted(true)
  }, [isLookingUpReferences])

  useEffect(() => {
    if (fromWallet && toWallet && fromBalance && toWalletExists) {
      onSuccess({ fromWallet, toWallet, fromBalance, toBalance })
    }
  }, [onSuccess, fromWallet, toWallet, fromBalance, toBalance, toWalletExists])

  // poll balances in case of wallet creation. A reference that doesn't resolve leaves
  // `toWallet` undefined, so only poll once the lookup has actually returned a wallet.
  useInterval(
    () => {
      if (!toRef) return
      dispatch(walletApiEvents.balance_request({ partyId: toRef }))
    },
    toRef && !toWalletExists ? 2000 : null,
  )

  return (
    <Formik
      initialValues={{ from: '', to: '' }}
      validationSchema={validationSchema}
      onSubmit={(values: any) => {
        setLookupStarted(false)
        setWallets(values)
      }}
    >
      {({ handleChange, handleSubmit, submitCount, errors, values }) => {
        const searchFormLoaderState = isLookingUpReferences
          ? 'submitting'
          : (submitCount > 0 && !isEmpty(errors)) || lookupFailed
          ? 'error'
          : undefined

        return (
          <FormLoader
            onSubmit={handleSubmit}
            state={searchFormLoaderState}
            submittingText="Fetching wallets for confirmation"
            errorText={lookupFailed ? 'Wallet reference not found' : 'Resolve errors'}
            buttonProps={{ children: 'Lookup Wallets' }}
          >
            <div className="rbn--row" style={{ marginBottom: '20px' }}>
              <div className="rbn--col rbn-col-md-4">
                <TextField
                  inputComponent={
                    <TextInput type={TextFieldTypes.text} name="from" value={values.from} onChange={handleChange} />
                  }
                  label="From Wallet"
                  error={errors.from}
                />
              </div>
              <div className="rbn--col rbn-col-md-4">
                <TextField
                  inputComponent={
                    <TextInput type={TextFieldTypes.text} name="to" value={values.to} onChange={handleChange} />
                  }
                  label="To Wallet"
                  error={errors.to}
                />
              </div>
              <div className="rbn--col rbn-col-md-4"></div>
            </div>
          </FormLoader>
        )
      }}
    </Formik>
  )
}

export default TransferReferenceLookup
